import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, type ShareLeadsPage, type ShareStatsView } from "../api.js";
import { ErrorNote } from "../components/ui.js";
import { dropOff, emailsFilename } from "./shareView.js";

type Range = "30d" | "all";

const tile = "flex flex-col gap-1 rounded-[12px] bg-bay-900 p-3";
const upper = (code: string) => code.toUpperCase();

/**
 * What the share has done: views, how far people got, and the emails
 * collected. The Export page gives it a tab of its own, named already, so
 * the heading is only drawn where it sits beside other things.
 */
export const ShareStats: React.FC<{ projectId: string; projectName: string; emailGate: boolean; title?: string }> = ({
  projectId,
  projectName,
  emailGate,
  title,
}) => {
  // All time first: the number the project nav shows beside Views.
  const [range, setRange] = useState<Range>("all");
  const [stats, setStats] = useState<ShareStatsView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;
    setError(null);
    api
      .shareStats(projectId, range)
      .then((next) => {
        if (current && alive.current) setStats(next);
      })
      .catch((cause: unknown) => {
        if (current && alive.current) setError(cause instanceof Error ? cause.message : "Could not load views");
      });
    return () => {
      current = false;
    };
  }, [projectId, range]);

  const segment = (active: boolean) =>
    `rounded-[8px] px-2.5 py-1.5 text-[13px] transition-colors ${
      active ? "bg-page font-semibold text-ink-100 shadow-sm" : "text-ink-200 hover:text-ink-100"
    }`;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {title ? <h3 className="text-[13px] font-semibold text-ink-100">{title}</h3> : null}
        <div role="group" aria-label="Range" className="inline-flex gap-0.5 rounded-[10px] bg-bay-800 p-[3px]">
          <button
            type="button"
            className={segment(range === "30d")}
            aria-pressed={range === "30d"}
            onClick={() => setRange("30d")}
          >
            Last 30 days
          </button>
          <button
            type="button"
            className={segment(range === "all")}
            aria-pressed={range === "all"}
            onClick={() => setRange("all")}
          >
            All time
          </button>
        </div>
      </div>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {stats && stats.views === 0 ? (
        <p className="text-[13px] text-ink-300">No views yet. Share the link or embed it to start counting.</p>
      ) : null}

      {stats && stats.views > 0 ? (
        <>
          <div className="grid grid-cols-3 gap-2">
            <div className={tile}>
              <span className="text-[12px] text-ink-300">Views</span>
              <span className="tnum text-[24px] font-semibold leading-none text-ink-100">{stats.views}</span>
            </div>
            <div className={tile}>
              <span className="text-[12px] text-ink-300">Viewers</span>
              <span className="tnum text-[24px] font-semibold leading-none text-ink-100">{stats.uniqueViewers}</span>
            </div>
            <div className={tile}>
              <span className="text-[12px] text-ink-300">Avg. watched</span>
              <span className="tnum text-[24px] font-semibold leading-none text-ink-100">{stats.averagePercent}%</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-[13px] font-semibold text-ink-200">How far people watched</span>
            {dropOff(stats).map((row) => (
              <div key={row.label} className="flex items-center gap-2.5 text-[12.5px]">
                <span className="tnum w-9 text-ink-300">{row.label}</span>
                <div
                  className="h-2 flex-1 overflow-hidden rounded-full bg-bay-800"
                  role="img"
                  aria-label={`${row.count} views reached ${row.label}`}
                >
                  <div className="h-full rounded-full bg-ember-500" style={{ width: `${row.percent}%` }} />
                </div>
                <span className="tnum w-20 shrink-0 text-right text-ink-100">
                  {row.count} · {row.percent}%
                </span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-ink-200">
            <span>
              {"By language: "}
              {Object.entries(stats.byLanguage)
                .sort((a, b) => b[1] - a[1])
                .map(([code, count]) => `${upper(code)} ${count}`)
                .join(" · ")}
            </span>
            <span className="tnum">
              Link {stats.bySurface.link} · Embed {stats.bySurface.embed}
            </span>
          </div>
        </>
      ) : null}

      {stats?.hasLeads || emailGate ? <Leads projectId={projectId} projectName={projectName} /> : null}
    </div>
  );
};

/** The emails collected, a page of 50 at a time, and the whole list as a CSV. */
const Leads: React.FC<{ projectId: string; projectName: string }> = ({ projectId, projectName }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ShareLeadsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    let current = true;
    setError(null);
    api
      .shareLeads(projectId, page)
      .then((next) => {
        if (current) setData(next);
      })
      .catch((cause: unknown) => {
        if (current) setError(cause instanceof Error ? cause.message : "Could not load emails");
      });
    return () => {
      current = false;
    };
  }, [projectId, page]);
  useEffect(() => load(), [load]);

  const download = async () => {
    setSaving(true);
    setError(null);
    try {
      const blob = await api.shareLeadsCsv(projectId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = emailsFilename(projectName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not download the emails");
    } finally {
      setSaving(false);
    }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-[13px] font-semibold text-ink-200">Emails{data ? ` · ${data.total}` : ""}</h3>
        <button type="button" className="btn btn-secondary btn-sm" disabled={saving} onClick={() => void download()}>
          {saving ? "Preparing…" : "Download CSV"}
        </button>
      </div>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {data && data.total === 0 ? <p className="text-[12.5px] text-ink-300">No emails yet.</p> : null}
      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12.5px]">
              <thead className="text-[11.5px] text-ink-300">
                <tr>
                  <th className="py-1 pr-3 font-medium">Email</th>
                  <th className="py-1 pr-3 font-medium">First watched</th>
                  <th className="py-1 pr-3 text-right font-medium">Views</th>
                  <th className="py-1 text-right font-medium">Furthest</th>
                </tr>
              </thead>
              <tbody>
                {data.leads.map((lead) => (
                  <tr key={lead.email} className="border-t border-[var(--edge)] text-ink-200">
                    <td className="max-w-[220px] truncate py-1.5 pr-3">{lead.email}</td>
                    <td className="tnum py-1.5 pr-3">{new Date(lead.firstSeenAt).toLocaleDateString()}</td>
                    <td className="tnum py-1.5 pr-3 text-right">{lead.views}</td>
                    <td className="tnum py-1.5 text-right">{lead.furthestPercent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 ? (
            <div className="flex items-center justify-end gap-2 text-[12px] text-ink-300">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span className="tnum">
                {page} of {pages}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={page >= pages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
